import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTrade } from '@/lib/trade';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import i18n from '@/lib/i18n';
import {
  CODE_PATTERN,
  PRICE_LIST_QUERY_KEY,
  PRICE_PATTERN,
  createPriceListItem,
  updatePriceListItem,
  type PriceListItem,
} from './price-list-api';

/* ---------------------------------------------------------------------------
   יצירה ועריכה של פריט מחירון, בדיאלוג אחד.

   בעריכה הקוד מוצג אך לא ניתן לשינוי. צ'קליסטים ותבניות שומרים אותו
   כמחרוזת, ושינוי שם היה מנתק את כולם בלי שגיאה — החשבונית פשוט
   הייתה מדלגת על השורות. השרת דוחה את השדה גם אם יישלח.
   --------------------------------------------------------------------------- */

/**
 * מקלדת עברית ומשתמשים ישראלים מקלידים לעתים קרובות פסיק עשרוני.
 * "149,90" אינו שגיאה אלא 149.90 — מתקנים בשקט במקום להעניש.
 */
const normalizePrice = (raw: string) => raw.trim().replace(',', '.');

const schema = z.object({
  code: z
    .string()
    .trim()
    .min(1, i18n.t('price-list:dialog.codeRequired'))
    .max(64, i18n.t('price-list:dialog.codeMax'))
    .regex(CODE_PATTERN, i18n.t('price-list:dialog.codePattern')),
  description: z
    .string()
    .trim()
    .min(1, i18n.t('price-list:dialog.descriptionRequired'))
    .max(200, i18n.t('price-list:dialog.descriptionMax')),
  price: z
    .string()
    .transform(normalizePrice)
    .refine((v) => v !== '', i18n.t('price-list:dialog.priceRequired'))
    .refine((v) => PRICE_PATTERN.test(v), i18n.t('price-list:dialog.priceInvalid')),
  isActive: z.boolean(),
  category: z.enum(['SERVICE', 'MATERIAL', 'HARDWARE']),
});

const CATEGORIES: Array<PriceListItem['category']> = ['SERVICE', 'MATERIAL', 'HARDWARE'];

type FormInput = z.input<typeof schema>;
type FormOutput = z.output<typeof schema>;

/** "149.9" ו-"149.90" הם אותו מחיר. השוואה כמחרוזת הייתה שולחת עדכון ריק. */
const samePrice = (a: string, b: string) => {
  const norm = (s: string) => {
    const [int = '0', frac = ''] = s.split('.');
    return `${int.replace(/^0+(?=\d)/, '')}.${frac.padEnd(2, '0')}`;
  };
  return norm(a) === norm(b);
};

export function PriceItemDialog({ item, onClose }: { item: PriceListItem | null; onClose: () => void }) {
  const isEdit = item !== null;
  const { t } = useTranslation(['price-list', 'common']);
  // התוויות לפי התחום: נגרייה — פרזול; מנהלת משרד — ציוד משרדי.
  const trade = useTrade();
  const toast = useToast();
  const queryClient = useQueryClient();

  const form = useForm<FormInput, unknown, FormOutput>({
    resolver: zodResolver(schema),
    defaultValues: {
      code: item?.code ?? '',
      description: item?.description ?? '',
      price: item?.price ?? '',
      isActive: item?.isActive ?? true,
      category: item?.category ?? 'SERVICE',
    },
  });
  const { errors } = form.formState;
  const isActive = form.watch('isActive');
  const deactivatingUsedCode = isEdit && item.isActive && !isActive && item.usedByTemplates > 0;

  const mutation = useMutation({
    mutationFn: (values: FormOutput) => {
      if (!isEdit) {
        return createPriceListItem({
          code: values.code,
          description: values.description,
          price: values.price,
          // SERVICE היא ברירת המחדל בשרת — לא שולחים אותה.
          ...(values.category !== 'SERVICE' && { category: values.category }),
        });
      }
      // רק מה שהשתנה: כך יומן הביקורת מתעד שינוי אמיתי ולא "עדכון"
      // של שלושה שדות שאיש לא נגע בהם.
      return updatePriceListItem(item.id, {
        ...(values.description !== item.description && { description: values.description }),
        ...(!samePrice(values.price, item.price) && { price: values.price }),
        ...(values.isActive !== item.isActive && { isActive: values.isActive }),
        ...(values.category !== item.category && { category: values.category }),
      });
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: [PRICE_LIST_QUERY_KEY] });
      toast.success(isEdit ? t('dialog.updated') : t('dialog.added'), `${saved.code} — ${saved.description}`);
      onClose();
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.status === 409) {
        form.setError('code', { type: 'server', message: t('dialog.codeExists') });
        return;
      }
      toast.error(t('common:errors.saveFailed'), error instanceof ApiError ? error.message : t('dialog.tryAgainShortly'));
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    if (isEdit) {
      const unchanged =
        values.description === item.description &&
        samePrice(values.price, item.price) &&
        values.isActive === item.isActive &&
        values.category === item.category;
      if (unchanged) {
        onClose();
        return;
      }
    }
    mutation.mutate(values);
  });

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent aria-describedby="price-item-desc">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('dialog.editTitle') : t('dialog.newTitle')}</DialogTitle>
          <DialogDescription id="price-item-desc">
            {isEdit ? t('dialog.editDescription') : t('dialog.newDescription')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => void onSubmit(e)} noValidate>
          <DialogBody>
            <div className="grid gap-4 sm:grid-cols-[1fr_10rem]">
              <Field
                label={t('dialog.code')}
                htmlFor="price-code"
                required={!isEdit}
                error={errors.code?.message}
                hint={isEdit ? t('dialog.codeHintEdit') : t('dialog.codeHintNew')}
              >
                <Input
                  id="price-code"
                  autoFocus={!isEdit}
                  autoComplete="off"
                  spellCheck={false}
                  readOnly={isEdit}
                  // מזהה — LTR גם בטופס עברי.
                  className="ltr-inline text-start read-only:bg-surface-sunken read-only:text-fg-muted"
                  invalid={Boolean(errors.code)}
                  {...form.register('code')}
                />
              </Field>

              <Field label={t('dialog.price')} htmlFor="price-amount" required error={errors.price?.message}>
                <Input
                  id="price-amount"
                  autoFocus={isEdit}
                  // text ולא number: שדה number בדפדפן מקבל "1e3", מעגל
                  // בשקט ומסרב לפסיק עשרוני. כאן כל תו נשמר כפי שהוקלד.
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  className="ltr-inline tabular text-start"
                  invalid={Boolean(errors.price)}
                  {...form.register('price')}
                />
              </Field>
            </div>

            <Field label={t('dialog.description')} htmlFor="price-description" required error={errors.description?.message}>
              <Input
                id="price-description"
                autoComplete="off"
                invalid={Boolean(errors.description)}
                {...form.register('description')}
              />
            </Field>

            <Field label={t('dialog.category')} htmlFor="price-category" hint={t('dialog.categoryHint')}>
              <Controller
                control={form.control}
                name="category"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="price-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c}>
                          {t(`categoriesByTrade.${trade.key}.${c}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Field>

            {isEdit ? (
              <div className="flex items-center justify-between gap-4 rounded-lg border border-border px-4 py-3">
                <div>
                  <label htmlFor="price-active" className="text-sm font-medium text-fg">
                    {t('dialog.active')}
                  </label>
                  <p className="text-2xs text-fg-muted">{t('dialog.activeHint')}</p>
                </div>
                <Controller
                  control={form.control}
                  name="isActive"
                  render={({ field }) => (
                    <Switch id="price-active" checked={field.value} onCheckedChange={field.onChange} />
                  )}
                />
              </div>
            ) : null}

            {deactivatingUsedCode ? (
              <div
                role="alert"
                className="flex gap-3 rounded-lg border border-warning-border bg-warning-subtle px-4 py-3 text-sm text-warning"
              >
                <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
                <p>{t('dialog.deactivateWarning', { count: item.usedByTemplates })}</p>
              </div>
            ) : null}
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {isEdit ? t('common:actions.save') : t('dialog.submitNew')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
